import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { hasServerUrl } from './constants/config'
import { ConnectPage } from './pages/ConnectPage'
import { LoginPage } from './pages/LoginPage'
import { AppShell } from './pages/AppShell'
import { SettingsPage } from './pages/SettingsPage'
import './styles/global.css'

// ─── Route guards ──────────────────────────────────────────────────────────────

// Must pick a server before anything else. Sits OUTSIDE the auth guards so a fresh
// device goes Connect → TOTP → app.
function RequireServer({ children }: { children: React.ReactNode }) {
  return hasServerUrl() ? <>{children}</> : <Navigate to="/connect" replace />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

// ─── App ───────────────────────────────────────────────────────────────────────
//
// Chat-first entry flow (Open WebUI style):
//   /            → app shell (sidebar + chat); auto-opens most recent chat
//   /c/:chatId   → app shell with a specific chat open
//   /settings    → settings (reachable from the sidebar footer)
//   /login       → TOTP login (logic unchanged)

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public: server URL entry (no server picked yet) */}
        <Route path="/connect" element={<ConnectPage />} />

        {/* Public: login / setup (needs a server first) */}
        <Route
          path="/login"
          element={<RequireServer><PublicRoute><LoginPage /></PublicRoute></RequireServer>}
        />

        {/* Protected: chat-first app shell */}
        <Route
          path="/"
          element={<RequireServer><ProtectedRoute><AppShell /></ProtectedRoute></RequireServer>}
        />

        {/* Protected: app shell with a chat open */}
        <Route
          path="/c/:chatId"
          element={<RequireServer><ProtectedRoute><AppShell /></ProtectedRoute></RequireServer>}
        />

        {/* Protected: settings */}
        <Route
          path="/settings"
          element={<RequireServer><ProtectedRoute><SettingsPage /></ProtectedRoute></RequireServer>}
        />

        {/* Legacy workspace routes → redirect into the shell */}
        <Route path="/w/:workspaceId/c/:chatId" element={<RequireServer><ProtectedRoute><AppShell /></ProtectedRoute></RequireServer>} />
        <Route path="/w/:workspaceId" element={<Navigate to="/" replace />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
